/**
 * Pure URL helpers for Story List tab / folder / sidebar selection.
 *
 * Shared by `useStoryListRoute` (components) and modal selectors that still
 * read `getLocation` until compose-app is fully retired.
 */

import {
  TAB_ANALYTICS,
  TAB_PUBLICATIONS,
  TAB_STORIES,
  TAB_TEMPLATES
} from '@opengov/stories-shared/constants';

import { EStoryLists, IStoryListMessages, IStoryListSelection, ITabOptions } from '../../../../types';

const FOLDER_ID_PATTERN = /\/stories\/[^/]+\/folders\/([^/]+)/;

export function parseStoryListFolderIdParam(pathname: string): string | undefined {
  return pathname.match(FOLDER_ID_PATTERN)?.[1];
}

export function getStoryListTabFromRoute(pathname: string, folderIdParam?: string): ITabOptions {
  if (pathname.includes('/templates') || pathname.includes('/create-from-template')) {
    return TAB_TEMPLATES;
  }
  if (pathname.includes('/publications') || /^\/stories\/[^/]+\/?$/.test(pathname)) {
    return TAB_PUBLICATIONS;
  }
  if (folderIdParam === TAB_ANALYTICS) {
    return TAB_ANALYTICS;
  }
  return TAB_STORIES;
}

export function isStoryListPath(pathname: string): boolean {
  return /^\/stories\/[^/]+\/?(?:\/(stories|drafts|folders|templates|create-from-template|publications|organize).*)?$/.test(pathname);
}

export function getStoryListFolderIdFromRoute(tab: ITabOptions, folderIdParam?: string): string | null {
  if (!folderIdParam || tab === TAB_ANALYTICS) {
    return null;
  }
  if (
    folderIdParam === EStoryLists.Stories ||
    folderIdParam === EStoryLists.Templates ||
    folderIdParam === EStoryLists.Publications ||
    folderIdParam === EStoryLists.Analytics
  ) {
    return null;
  }
  return folderIdParam;
}

export function getStoryListSelectionFromRoute(
  tab: ITabOptions,
  folderId: string | null,
  messages: IStoryListMessages
): IStoryListSelection {
  switch (tab) {
    case TAB_TEMPLATES:
      return { id: TAB_TEMPLATES, displayName: messages.templates };
    case TAB_PUBLICATIONS:
      return { id: TAB_PUBLICATIONS, displayName: messages.publications };
    case TAB_ANALYTICS:
      return { id: TAB_ANALYTICS, displayName: messages.analytics };
    default:
      if (folderId) {
        return { id: folderId, displayName: folderId };
      }
      return { id: EStoryLists.Stories, displayName: messages.allStories };
  }
}

export function deriveStoryListRoute(pathname: string, folderIdParam?: string) {
  const tab = getStoryListTabFromRoute(pathname, folderIdParam);
  const currentFolderId = getStoryListFolderIdFromRoute(tab, folderIdParam);
  return { tab, currentFolderId };
}
